import type { IBuzzBridgeProfileResponse } from './types';

type GroupType = 'active_jobs' | 'requests' | 'invitations';

/**
 * Tab fetches use `group_type` and return sparse influencer rows. Without merging, the other
 * arrays are wiped in Redux. Preserve non-fetched slices from the previous profile.
 */
export function mergeBuzzBridgeProfileGroupResponse(
    prev: IBuzzBridgeProfileResponse | null | undefined,
    next: IBuzzBridgeProfileResponse,
    groupType: GroupType,
): IBuzzBridgeProfileResponse {
    if (!prev?.data?.influencer) return next;

    const infPrev = prev.data.influencer;
    const infNext = next.data.influencer;

    const mergedInfluencer = {
        ...infPrev,
        ...infNext,
        active_jobs: groupType === 'active_jobs' ? infNext.active_jobs ?? [] : infPrev.active_jobs ?? [],
        requests: groupType === 'requests' ? infNext.requests ?? [] : infPrev.requests ?? [],
        invitations: groupType === 'invitations' ? infNext.invitations ?? [] : infPrev.invitations ?? [],
    };

    if (groupType === 'active_jobs') {
        mergedInfluencer.active_jobs_count =
            infNext.active_jobs_count ?? mergedInfluencer.active_jobs?.length ?? infPrev.active_jobs_count ?? 0;
    }
    if (groupType === 'requests') {
        mergedInfluencer.requests_count =
            infNext.requests_count ?? mergedInfluencer.requests?.length ?? infPrev.requests_count ?? 0;
    }
    if (groupType === 'invitations') {
        mergedInfluencer.invitations_count =
            infNext.invitations_count ?? mergedInfluencer.invitations?.length ?? infPrev.invitations_count ?? 0;
    }

    const mergeTriplet = <T extends { active_jobs: unknown; requests: unknown; invitations: unknown }>(
        nextVal: T | undefined,
        prevVal: T | undefined,
    ): T =>
        ({
            active_jobs:
                groupType === 'active_jobs' ? nextVal?.active_jobs ?? prevVal?.active_jobs : prevVal?.active_jobs,
            requests: groupType === 'requests' ? nextVal?.requests ?? prevVal?.requests : prevVal?.requests,
            invitations:
                groupType === 'invitations' ? nextVal?.invitations ?? prevVal?.invitations : prevVal?.invitations,
        }) as T;

    return {
        ...next,
        data: { influencer: mergedInfluencer },
        totalCount: mergeTriplet(next.totalCount, prev.totalCount),
        totalPages: mergeTriplet(next.totalPages, prev.totalPages),
        hasNextPage: mergeTriplet(next.hasNextPage, prev.hasNextPage),
        hasPreviousPage: mergeTriplet(next.hasPreviousPage, prev.hasPreviousPage),
        nextPage: mergeTriplet(next.nextPage, prev.nextPage),
        previousPage: mergeTriplet(next.previousPage, prev.previousPage),
        results: mergeTriplet(next.results, prev.results),
    };
}
